import { useEffect, useRef, useState } from 'react';
import { getInitialsFromName, formatTime, useConnectionStatus } from '../../utils/miscUtils';
import { toast } from 'react-toastify';
import { useMediaContext } from '../../context/P2PVideoConsultation/MediaContext';
import { useSignalingContext } from '../../context/P2PVideoConsultation/SignalingContext';
import { SDPAnswer } from './interfaces';
import { getOnlineUsers } from '../../store/slices/chat';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  IAccountInfo,
  IPerson,
  selectAccountInfo,
  selectPersonLoggedIn,
} from '../../store/slices/auth';
import { createConsultationRoom } from '../../store/slices/workOrderServiceConsultation';
import { SalesOrderInfo } from '../../store/slices/salesOrders';
import { client } from '../../networking/client';
import EmptyScreen from '../VideoConsultingRoom/EmptyScreen';
import styles from './VideoConsultationP2P.module.scss';
import POSITION = toast.POSITION;
import { useTranslation } from '../../hooks/useTranslations';

export default function VideoConsultationP2P({
  selectedSalesOrder,
}: {
  selectedSalesOrder: SalesOrderInfo;
}) {
  const {
    handleMic,
    handleCam,
    remoteMedia,
    localMedia,
    localAudio,
    localVideo,
    stream,
    setStream,
  } = useMediaContext();

  const {
    socket,
    iceServerData,
    handleIceCandidate,
    onlineUsersInRoom,
    setOnlineUsersInRoom,
    connectionState,
    peerConnection,
    setConnectionState,
    setPeerConnection,
    setRoomId,
    roomId,
  } = useSignalingContext();
  const { isOffline, isConnectionPoor } = useConnectionStatus();
  const { translations } = useTranslation();
  const personLoggedIn = useAppSelector<IPerson>(selectPersonLoggedIn);
  const accountInfo: IAccountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const taId = selectedSalesOrder.sales_order?.ta_id ?? '';
  const productOrderId = selectedSalesOrder.sales_order?.sales_order_external_id ?? '';
  const productOrderItemId =
    selectedSalesOrder.sales_order_item?.external_sales_order_item_id ?? '';
  const userId = accountInfo.external_person_id;
  const [callEnded, setCallEnded] = useState<boolean>(false);
  // const createRoomResponse = useAppSelector(selectCreateRoomResponse);

  const [timer, setTimer] = useState<number>(0);
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const calling = useRef(false);
  const dispatch = useAppDispatch();

  // const loadingText = () => {
  //   let text = '';
  //   if (connectionState === '') {
  //     text = 'Waiting for the guest to join.';
  //   }
  //   if (connectionState === 'connecting') {
  //     text = 'Connecting...';
  //   }
  //   if (connectionState === 'no-sdp-offer') {
  //     text = 'No offer for joining the call.';
  //   }
  //   return text;
  // };
  const loadingText = () => {
    if (connectionState === '') {
      return translations('videoCall.waiting_guest');
    }
    if (connectionState === 'connecting') {
      return translations('videoCall.connecting');
    }
    if (connectionState === 'no-sdp-offer') {
      return translations('videoCall.no_offer');
    }
    return '';
  };

  const handleCloseCall = () => {
    stream?.getTracks().forEach(function (track: any) {
      track.stop();
    });
    peerConnection?.close();
    socket.disconnect();
    setCallEnded(true);
  };

  const handleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const startConsulatation = async () => {
    const getSalesOrderAppointmentResponse = await client.get(
      `/healthpoint/sales-order-appointment/${selectedSalesOrder?.sales_order_appointment.sales_order_appointment_id}`
    );
    if (!getSalesOrderAppointmentResponse?.data.salesOrderAppointment?.access_key) {
      const createRoomResponse = await dispatch(
        createConsultationRoom({
          accNumber: accountInfo.account_number,
          taId: taId,
          productOrderId: productOrderId,
          productOrderItemId: productOrderItemId,
        })
      );
      setRoomId(createRoomResponse.payload.data.payload.room_id);
      await client.put(
        `/healthpoint/sales-order-appointment/eyeson/${selectedSalesOrder?.sales_order_appointment.sales_order_appointment_id}`,
        {
          accessKey: createRoomResponse.payload.data.payload.room_id,
          guestToken: '',
        }
      );
    } else {
      setRoomId(getSalesOrderAppointmentResponse?.data?.salesOrderAppointment?.access_key);
    }
  };

  useEffect(() => {
    if (!('mediaDevices' in navigator) || !navigator.mediaDevices.getUserMedia) {
      console.log('mediaDevices error');
      return;
    }
    navigator.mediaDevices.getUserMedia({ audio: true, video: true }).then((streamObj) => {
      setStream(streamObj);
    });

    startConsulatation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (connectionState === 'connected') {
      const interval = setInterval(() => setTimer((timer) => timer + 1), 1000);
      return () => clearInterval(interval);
    }
  }, [connectionState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOffline || isConnectionPoor) {
      toast.error(translations('label.poor_connection'), { position: POSITION.BOTTOM_RIGHT });
    }
  }, [isOffline, isConnectionPoor]);

  useEffect(() => {
    if (iceServerData && stream && roomId) {
      setTimeout(() => {
        dispatch(getOnlineUsers(roomId)).then((res: any) => {
          const {
            data: { payload },
          } = res.payload;
          setOnlineUsersInRoom(payload.online_users.filter((el: any) => el.person_id !== userId));
        });
      }, 1000);

      const initConnection = async () => {
        try {
          localMedia.current.srcObject = stream;

          const _peer = new RTCPeerConnection({
            iceServers: iceServerData,
          });
          setTimeout(() => {
            // Add local stream to RTCPeerConnection
            stream.getTracks().forEach((track: any) => {
              _peer.addTrack(track, stream);
            });

            _peer.onicecandidate = (e: any) => {
              handleIceCandidate(e, calling.current);
            };

            _peer.ontrack = (e: any) => {
              remoteMedia.current.srcObject = e.streams[0];
            };

            _peer.onconnectionstatechange = (e: any) => {
              // if (_peer.connectionState === 'disconnected') {
              //   // openSnackbar("User is disconnected from the call.");
              //   handleCloseCall();
              // }

              // if (_peer.connectionState === 'failed') {
              //   // openSnackbar("User is disconnected from the call.");
              //   handleCloseCall();
              // }
              setConnectionState(_peer.connectionState);
            };

            _peer.oniceconnectionstatechange = (e: any) => {
              // console.log("iceConnectionState", peerConnection.iceConnectionState);
            };
            setPeerConnection(_peer);
          }, 2000);
        } catch (error) {
          console.error('Error accessing media devices:', error);
        }
      };

      initConnection();
    }
  }, [iceServerData !== null, stream !== undefined, roomId !== '']); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (peerConnection && roomId) {
      // ****** SOCKET EVENTS ******
      socket.emit('join_room', { room_id: roomId, person_id: userId });

      socket.on('receive_sdp_answer', (data: SDPAnswer) => {
        peerConnection.setRemoteDescription(new RTCSessionDescription(JSON.parse(data.sdp_answer)));
      });

      socket.on('receive_candidate_answer', (data: RTCIceCandidateInit | undefined) => {
        peerConnection.addIceCandidate(new RTCIceCandidate(data));
      });

      socket.on('response_close_sdp_offer', (data: any) => {
        peerConnection?.close();
        stream?.getTracks().forEach(function (track: any) {
          track.stop();
        });
        // router.push('/')
      });

      socket.on('error_close_sdp_offer', (err: any) => {
        console.log('error_close_sdp_offer', err);
      });
    }
  }, [peerConnection !== null, roomId !== '']); // eslint-disable-line react-hooks/exhaustive-deps

  // HANDLE NEGOTIANTION (Check if the patient joined the call in order so send the sdp_offer)
  useEffect(() => {
    if (onlineUsersInRoom.length > 0) {
      const handleNegotiationNeeded = async (_peer: any) => {
        try {
          calling.current = true;
          const sdp = await _peer.createOffer();
          await _peer.setLocalDescription(sdp);
          socket.emit('sdp_offer', {
            room_id: roomId,
            person_id: userId,
            sdp_offer: JSON.stringify(sdp),
          });
        } catch (error) {
          console.error('Error creating offer:', error);
        }
      };
      handleNegotiationNeeded(peerConnection);
    }
  }, [onlineUsersInRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLocalVideoClassName = () => {
    if (localVideo) {
      if (onlineUsersInRoom.length === 0) return `${styles.localVideo} ${styles.fullScreen}`;
      return `${styles.localVideo}`;
    }
    return `${styles.localVideo} ${styles.hide}`;
  };

  return (
    <div
      className={
        !fullScreen ? `${styles.videoContainer}` : `${styles.videoContainer} ${styles.fullScreen}`
      }
      style={!fullScreen ? { position: 'relative' } : {}}
    >
      {['no-sdp-offer', 'connecting', ''].includes(connectionState) && (
        <div id="cover-spin" style={{ position: 'absolute' }}>
          {loadingText()}
        </div>
      )}
      {!callEnded ? (
        <>
          <div className={styles.videoCallWrapper}>
            <div className={styles.mainSection}>
              <div
                className={styles.remoteVideoWrapper}
                style={onlineUsersInRoom.length === 0 ? { display: 'none' } : {}}
              >
                <video
                  ref={remoteMedia}
                  className={
                    remoteMedia ? `${styles.remoteVideo}` : `${styles.remoteVideo} ${styles.hide}`
                  }
                  autoPlay
                  playsInline
                />
                <div
                  className={
                    remoteMedia ? `${styles.emptyState} ${styles.hide}` : `${styles.emptyState}`
                  }
                >
                  <div className={styles.avatar}> </div>
                </div>
              </div>
              <div
                className={
                  onlineUsersInRoom.length === 0
                    ? `${styles.localVideoWrapper} ${styles.fullScreen}`
                    : styles.localVideoWrapper
                }
              >
                <video
                  ref={localMedia}
                  className={setLocalVideoClassName()}
                  autoPlay
                  playsInline
                  muted
                />
                <div
                  className={
                    localVideo ? `${styles.emptyState} ${styles.hide}` : `${styles.emptyState}`
                  }
                >
                  <div className={styles.avatar}>
                    {getInitialsFromName(
                      `${personLoggedIn.person_firstname} ${personLoggedIn.person_lastname}`
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.actionsWrapper}>
            <div className={styles.timeStamp}>{formatTime(timer)}</div>
            <div className={styles.actions}>
              <button onClick={handleMic} className={`${styles.btn}`}>
                <i className={`${localAudio ? styles.micOnIcon : styles.micOffIcon}`} />
              </button>
              <button
                onClick={handleCam}
                className={localVideo ? `${styles.btn}` : `${styles.btn} ${styles.red}`}
              >
                <i className={localVideo ? styles.videoWhiteIcon : styles.videoOff} />
              </button>
              <button onClick={handleCloseCall} className={`${styles.btn} ${styles.red}`}>
                <i className={styles.endCallWhiteIcon} />
              </button>
            </div>
            <div className={styles.actions}>
              <button onClick={handleFullScreen} className={`${styles.btn}`}>
                {fullScreen ? (
                  <i className={styles.fullScreenIconOff} />
                ) : (
                  <i className={styles.fullScreenIconOn} />
                )}
              </button>
            </div>
          </div>
        </>
      ) : (
        <EmptyScreen
          style={{ height: 'calc(100vh - 180px)' }}
          fullName={`${selectedSalesOrder?.customer_person?.person_firstname} ${selectedSalesOrder?.customer_person?.person_lastname}`}
        />
      )}
    </div>
  );
}
